import { useQuery } from '@tanstack/react-query'

import type { ClinicId, ClinicScheduleDay } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Fetches the derived per-day working-vet schedule for a clinic over a date range.
 * `from` is inclusive, `to` exclusive (YYYY-MM-DD, clinic wall-clock) — matching
 * the calendar's date-range convention. The rota itself lives on the backend; this
 * is the only way the frontend learns who works when.
 *
 * Pass `includeOverrides: false` to fetch the raw rotation without DB overrides applied
 * (used to show the regular rota as a muted layer under the override calendar).
 */
export function useClinicSchedule(opts: {
  clinicId: ClinicId | undefined
  /** Start date (YYYY-MM-DD, inclusive). Query is disabled until from, to, and clinicId are set. */
  from?: string
  /** End date (YYYY-MM-DD, exclusive). Query is disabled until from, to, and clinicId are set. */
  to?: string
  /**
   * When false, returns the raw rotation without DB overrides applied.
   * Defaults to true (effective schedule with overrides).
   * Included in the query key so the two variants cache independently.
   */
  includeOverrides?: boolean
}): { data: ClinicScheduleDay[] | undefined; isLoading: boolean; isError: boolean } {
  const api = useOsApi()
  const { clinicId, from, to, includeOverrides } = opts

  // Resolve the effective flag for the query key — always store a concrete boolean
  // so the key is stable across renders where the prop is omitted.
  const effectiveIncludeOverrides = includeOverrides ?? true

  const query = useQuery({
    queryKey: ['clinic-schedule', clinicId, from, to, effectiveIncludeOverrides],
    enabled: !!clinicId && !!from && !!to,
    queryFn: async () => {
      // Single-clinic request expressed as a one-element clinicIds filter (the
      // endpoint's sole clinic param); the response is unwrapped to that clinic below.
      const response = await api.get('/schedules', {
        query: {
          from: from!,
          to: to!,
          clinicIds: [clinicId!],
          // Only sent when opting out — the server defaults to true.
          includeOverrides: effectiveIncludeOverrides ? undefined : 'false',
        },
      })
      if (!response.success) throw new Error(response.error.message)
      // Single-clinic request → exactly one grouped entry; unwrap to its days.
      return response.data.schedules[0]?.days ?? []
    },
  })

  return { data: query.data, isLoading: query.isLoading, isError: query.isError }
}
